import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Button, Callout, FormLink, SectionTitle, ServiceCard, PriceBadge, Tag, SearchInput } from '@azoya-spa/ui'
import '@azoya-spa/ui/styles.css'
import {
  buildSearchIndex,
  computedServiceLabel,
  searchCatalogIndex,
  startingPrice,
  formatCents,
} from '@azoya-spa/contracts'
import { useCatalog } from '../../hooks/useCatalog'
import { useSiteContent } from '../../hooks/useSiteContent'
import practicalInfoFallback from '../../api/fallback/practicalInfo.json'
import { frenchNumberToTelHref } from '../../utils/phone'
import { PARAM_UNIVERS } from '../../utils/universDeSoins'
import { ServicePrice } from './ServicePrice'
import { SUGGESTIONS } from './suggestions'
import { bookingCtaClicked, phoneClicked, serviceFamilyOpened } from '../../analytics/events'
import './Services.css'

// Le lien de l'encart mène toujours à une page du site : c'est le `Link` du
// routeur, jamais une ancre nue qui rechargerait l'application. Sa forme suit
// le traitement — un bouton clair sur la surface sombre d'une règle, un lien
// souligné sur la surface claire d'un conseil, qui ne concurrence pas
// « Réserver ».
function LienDeLEncart({ encart }) {
  if (!encart.lien) return null
  const { libelle, destination } = encart.lien
  if (encart.traitement === 'regle') {
    return (
      <Button as={Link} to={destination} variant="light">
        {libelle}
      </Button>
    )
  }
  return (
    <FormLink as={Link} to={destination}>
      {libelle}
    </FormLink>
  )
}

/* Hors du composant : l'effet d'arrivée ci-dessous s'en sert, et une fonction
   redéclarée à chaque rendu l'aurait fait se rejouer sans fin. */
function amenerEnVue(element) {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  element?.scrollIntoView({
    behavior: prefersReducedMotion ? 'auto' : 'smooth',
    block: 'start',
  })
}

function Services() {
  const { catalog, status: statutDuCatalogue } = useCatalog()
  // Le numéro de l'impasse de recherche est le même que celui du pied de page,
  // et vient donc de la même source : la maquette l'écrit en dur, mais c'est
  // la gérante qui le possède depuis « Informations pratiques ». Codé ici, il
  // se serait mis à mentir le jour où elle le change.
  const infosPratiques = useSiteContent('practical-info', practicalInfoFallback)
  const [searchParams, setSearchParams] = useSearchParams()
  const [mode, setMode] = useState('browse')
  const [query, setQuery] = useState('')
  const detailPanelRef = useRef(null)


  /* L'univers ouvert vit dans l'adresse et non dans un état local : d'autres
     écrans y envoient sur une famille précise — la page introuvable en propose
     la grille — et une adresse se partage. Un état local en doublon aurait
     laissé l'adresse mentir dès le premier clic. */
  const activeFamilyId = searchParams.get(PARAM_UNIVERS)

  // Le catalogue arrive de l'API : l'identifiant de la famille active ne peut
  // plus être initialisé à la construction du module. On retombe sur la
  // première famille tant que rien n'est sélectionné — et aussi quand l'adresse
  // désigne une famille que l'administration a depuis supprimée ou masquée.
  const activeFamily = catalog.find((fam) => fam.id === activeFamilyId) ?? catalog[0] ?? null

  /* Arrivée avec un univers déjà désigné. Le panneau étant sous la grille, il
     est hors écran à l'ouverture : sans ce défilement, le lien « Épilation »
     dépose la visiteuse en haut d'une page où l'épilation est mille pixels
     plus bas — le symptôme même que le défilement au clic existe pour éviter.
     Contrairement à handleRelance, pas d'attente d'image ici : le panneau est
     déjà monté, et ScrollToTop — qui remet la fenêtre en haut au changement de
     page — est rendu avant <App/> dans index.js, donc son effet passe avant
     celui-ci. L'univers d'arrivée est figé au montage : relu à chaque rendu,
     chaque clic ferait défiler deux fois. */
  const [universDemandeALArrivee] = useState(() => searchParams.get(PARAM_UNIVERS))

  useEffect(() => {
    if (!universDemandeALArrivee) return
    amenerEnVue(detailPanelRef.current)
  }, [universDemandeALArrivee])

  /* La même arrivée, côté mesure. Une famille s'ouvre ici sans traverser
     handleFamilySelect ni handleRelance : le cahier des charges disait que
     `service_family_opened` ne part jamais au chargement, ce qui était vrai
     tant qu'un chargement ne pouvait ouvrir que la famille par défaut — que
     personne n'avait demandée. Depuis que l'univers vit dans l'adresse, une
     arrivée est un choix : celui d'un lien partagé, ou d'une pastille de la
     page introuvable. D'où une troisième origine, `adresse`, distincte de
     `grille` : ce n'est pas la même chose que choisir dans la grille.

     Adossé à `universDemandeALArrivee`, figé au montage, et NON à
     `searchParams` : chaque clic sur une famille réécrit l'adresse, et un effet
     qui la relirait émettrait une seconde arrivée par-dessus le `grille` de
     handleFamilySelect. Même raisonnement, mot pour mot, que pour le
     défilement ci-dessus.

     Le ref est nécessaire parce que le catalogue arrive de l'API et remplace
     la copie embarquée par un tableau neuf, ce qui relance cet effet sur la
     même arrivée. */
  const arriveeComptee = useRef(null)

  useEffect(() => {
    if (!universDemandeALArrivee) return
    if (arriveeComptee.current === universDemandeALArrivee) return
    /* `useSiteContentState` ne rend jamais un catalogue vide : son état initial
       est le cache, sinon la copie embarquée à la compilation. L'asynchronie
       n'est donc pas « vide → API » mais « instantané périmé → API », et seul
       `status` les distingue. Trancher pendant que la requête est en vol, c'est
       compter d'après un catalogue que l'API va peut-être contredire : la
       famille demandée peut y figurer alors qu'elle vient d'être supprimée, ou
       en être absente alors qu'elle vient d'être créée. On attend.

       `error` ne fait pas attendre : la requête a définitivement échoué, la
       copie embarquée est ce que la visiteuse verra, et le nom émis est alors
       bien celui de son écran. */
    if (statutDuCatalogue === 'pending') return

    const familleDemandee = catalog.find((fam) => fam.id === universDemandeALArrivee)
    /* L'adresse peut nommer une famille que l'administration a supprimée ou
       masquée depuis ; la page retombe alors sur la première du catalogue.
       Émettre SON nom serait un mensonge : la visiteuse a demandé X et voit Y.
       On n'émet rien plutôt qu'un chiffre faux.

       Le ref n'est posé qu'APRÈS cette sortie, pour ne jamais clôturer une
       arrivée sur un échec de résolution. C'est de la défense en profondeur,
       et il faut le dire : l'attente ci-dessus suffit déjà, et AUCUN test ne
       tient cet ordre — l'inverser laisse la suite verte, mesuré. Le seul
       chemin qui le rendrait vivant serait un `status` repassé à `success`
       après un `error`, ce que seul `reessayer` produit et que cette page
       n'appelle pas. */
    if (!familleDemandee) return

    arriveeComptee.current = universDemandeALArrivee
    // La propriété `famille` porte le NOM affiché, jamais l'identifiant : c'est
    // ce qu'un humain lit dans un entonnoir.
    serviceFamilyOpened(familleDemandee.name, 'adresse')
  }, [universDemandeALArrivee, catalog, statutDuCatalogue])

  // Le schéma n'impose aucune longueur minimale à `groups` ni à `services` :
  // un groupe visible peut ne contenir aucune prestation visible (groupe créé
  // avant d'être rempli, ou prestations masquées une à une sans masquer le
  // groupe). Une pastille suivie de rien n'a rien à dire à une visiteuse — on
  // ne rend pas du tout ces groupes-là, plutôt que de les filtrer au niveau du
  // rendu de chaque prestation.
  const visibleGroups = activeFamily?.groups.filter((group) => group.services.length > 0) ?? []

  // L'index de recherche se reconstruit au seul changement de catalogue : la
  // normalisation des ~150 prestations n'a pas à être refaite à chaque frappe.
  // Sa construction et son interrogation vivent dans les contrats
  // (catalogSearch.ts), avec le champ `keywords` qu'elles consomment.
  const flatIndex = useMemo(() => buildSearchIndex(catalog), [catalog])

  /* Le panneau de détail est sous la grille des familles, donc presque toujours
     hors écran au moment du clic : sans ce défilement, choisir une famille n'a
     aucun effet visible. On le déclenche ici et non dans un useEffect sur
     l'univers ouvert, qui se déclencherait à CHAQUE montage — y compris sur une
     arrivée ordinaire sur Services, où la visiteuse n'a demandé aucun univers
     et n'a rien à faire plus bas. Ce défilement-là est celui de l'effet
     d'arrivée ci-dessus, réservé aux adresses qui nomment un univers. */
  const handleFamilySelect = (familyId, familyName) => {
    serviceFamilyOpened(familyName, 'grille')
    ouvrirLUnivers(familyId)
    amenerLePanneauEnVue()
  }

  /* On écrit dans une COPIE des paramètres présents : la visiteuse arrivée
     d'une campagne garde ses `utm_*`, qu'un objet neuf aurait effacés. Et on
     REMPLACE l'entrée d'historique, sans quoi quatre univers parcourus
     demanderaient quatre retours en arrière avant de ressortir de la page. */
  function ouvrirLUnivers(familyId) {
    const parametres = new URLSearchParams(searchParams)
    parametres.set(PARAM_UNIVERS, familyId)
    setSearchParams(parametres, { replace: true })
  }

  /* Depuis l'impasse de recherche : on change AUSSI de mode. Le panneau de
     détail n'existe pas dans le DOM tant que « Parcourir » n'est pas rendu, si
     bien que le défilement de handleFamilySelect ne trouverait rien à viser —
     d'où l'attente d'une image, une fois le nouveau mode peint. Sans elle, le
     clic ramène la visiteuse en haut d'une page dont la partie utile est plus
     bas : exactement l'effet que le défilement ci-dessus existe pour éviter. */
  const handleRelance = (familyId, familyName) => {
    serviceFamilyOpened(familyName, 'relance_recherche')
    setMode('browse')
    ouvrirLUnivers(familyId)
    requestAnimationFrame(amenerLePanneauEnVue)
  }

  function amenerLePanneauEnVue() {
    amenerEnVue(detailPanelRef.current)
  }

  const hasQuery = query.trim().length > 0
  const searchResults = hasQuery ? searchCatalogIndex(flatIndex, query) : []
  const resultsCountLabel = `${searchResults.length} ${searchResults.length === 1 ? 'résultat' : 'résultats'} pour « ${query} »`

  if (catalog.length === 0) {
    return (
      <div className="services-page">
        <p className="services-no-results">Nos tarifs sont momentanément indisponibles.</p>
      </div>
    )
  }

  return (
    <div className="services-page">
      <div className="services-hero-wrap">
        <div className="services-hero">
          <div className="services-hero-badge">✦ Notre carte de soins</div>
          <h1 className="services-hero-title">
            Nos <span>prestations</span>
          </h1>
          <p className="services-hero-subtitle">
            Explorez nos soins par univers, ou trouvez directement ce que vous cherchez.
          </p>
        </div>
      </div>

      <div className="services-mode-switch-wrap">
        <div className="services-mode-switch">
          <button type="button" className={mode === 'browse' ? 'is-active' : ''} onClick={() => setMode('browse')}>
            Parcourir
          </button>
          <button type="button" className={mode === 'search' ? 'is-active' : ''} onClick={() => setMode('search')}>
            Rechercher
          </button>
        </div>
      </div>

      <div className="services-content">
        {mode === 'browse' && (
          <div>
            <SectionTitle>Nos univers de soins</SectionTitle>
            <p className="services-browse-subtitle">
              Choisissez un univers pour découvrir ses catégories et tarifs.
            </p>

            <div className="services-family-grid">
              {catalog.map((fam) => {
                const depart = startingPrice(fam)
                return (
                  <ServiceCard
                    key={fam.id}
                    as="button"
                    type="button"
                    icon={fam.icon}
                    onClick={() => handleFamilySelect(fam.id, fam.name)}
                    className={'services-family-card' + (fam.id === activeFamily?.id ? ' is-active' : '')}
                  >
                    <span className="services-family-name">{fam.name}</span>
                    <span className="services-family-tagline">{fam.tagline}</span>
                    <span className="services-family-price">
                      <PriceBadge
                        label="dès"
                        amount={depart ? `${formatCents(depart.cents)}${depart.perPerson ? '/pers.' : ''}` : '—'}
                      />
                    </span>
                  </ServiceCard>
                )
              })}
            </div>

            <div className="services-detail-panel" ref={detailPanelRef}>
              <div className="services-detail-header">
                <span className="services-detail-icon">{activeFamily?.icon}</span>
                <div>
                  <h2 className="services-detail-title">{activeFamily?.name}</h2>
                  <p className="services-detail-tagline">{activeFamily?.tagline}</p>
                </div>
              </div>

              {activeFamily?.encart && (
                <div className="services-detail-encart">
                  <Callout
                    id="services-encart"
                    variant={activeFamily.encart.traitement === 'regle' ? 'rule' : 'advice'}
                    title={activeFamily.encart.titre}
                    link={<LienDeLEncart encart={activeFamily.encart} />}
                  >
                    {activeFamily.encart.corps}
                  </Callout>
                </div>
              )}

              {visibleGroups.length === 0 ? (
                <p className="services-no-results">Les tarifs de cet univers sont momentanément indisponibles.</p>
              ) : (
                visibleGroups.map((group) => (
                  <div key={group.id} className="services-category-block">
                    <Tag variant="coral">{group.name}</Tag>
                    <div className="services-item-grid">
                      {group.services.map((service) => (
                        <div key={service.id} className="services-item-row">
                          <span className="services-item-name">
                            {computedServiceLabel(service)}
                            {service.duration && <span className="services-item-duration"> · {service.duration}</span>}
                            {service.isExtra && <span className="services-extra-tag">Extra</span>}
                          </span>
                          <span className="services-price">
                            <ServicePrice amounts={service.amounts} />
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {mode === 'search' && (
          <div>
            <div className="services-search-wrap">
              <SearchInput
                label="Rechercher un soin ou une catégorie"
                value={query}
                onChange={setQuery}
                placeholder="Rechercher un soin, une catégorie…"
              />
            </div>

            {!hasQuery && (
              <div className="services-suggestions">
                <div className="services-suggestions-label">Recherches fréquentes</div>
                <div className="services-suggestions-list">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} type="button" onClick={() => setQuery(s)}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {hasQuery && searchResults.length > 0 && (
              <>
                <p className="services-results-count">{resultsCountLabel}</p>
                <div className="services-results-list">
                  {searchResults.map(({ family, group, service, label }) => (
                    <div key={service.id} className="services-result-row">
                      <div>
                        <div className="services-result-name">
                          {label}
                          {service.isExtra && <span className="services-extra-tag">Extra</span>}
                        </div>
                        <div className="services-result-context">
                          {group.name} · {family.name}
                        </div>
                      </div>
                      <span className="services-price">
                        <ServicePrice amounts={service.amounts} />
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* L'impasse. Elle ne renvoie plus la visiteuse deviner un autre
                mot : elle nomme les deux causes possibles, rouvre les six
                univers, et propose de demander à quelqu'un — le cas le plus
                fréquent étant une prestation que l'institut ne propose pas,
                qu'aucune reformulation ne fera apparaître. */}
            {hasQuery && searchResults.length === 0 && (
              <div className="services-impasse">
                {/* `role="status"` avec l'annonce polie : la maquette ne pose
                    que `aria-live`, qui laisse la région sans rôle. Le couple
                    est la forme canonique, et c'est lui qui donne au lecteur
                    d'écran une région nommée plutôt qu'un texte qui surgit. */}
                <div className="services-impasse-constat" role="status" aria-live="polite">
                  <div className="services-impasse-icon" aria-hidden="true">✦</div>
                  <p className="services-impasse-titre">Aucun résultat pour « {query} ».</p>
                  <p className="services-impasse-explication">
                    Ce soin ne fait peut-être pas partie de nos prestations, ou porte un autre nom
                    dans notre catalogue.
                  </p>
                </div>

                <div>
                  <div className="services-impasse-relance-titre">Découvrez nos univers de soins</div>
                  <div className="services-relance-grid">
                    {catalog.map((fam) => (
                      <button
                        key={fam.id}
                        type="button"
                        className="services-relance-card"
                        onClick={() => handleRelance(fam.id, fam.name)}
                      >
                        <span className="services-relance-icon" aria-hidden="true">{fam.icon}</span>
                        <span>
                          <span className="services-relance-name">{fam.name}</span>
                          <span className="services-relance-tagline">{fam.tagline}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="services-impasse-contact">
                  <p>
                    Vous ne trouvez pas ce que vous cherchez ? Appelez l&apos;institut, on vous
                    répond en quelques minutes — de vive voix, c&apos;est souvent plus rapide.
                  </p>
                  <Button
                    as="a"
                    href={frenchNumberToTelHref(infosPratiques.phone)}
                    onClick={() => phoneClicked()}
                  >
                    Appeler — {infosPratiques.phone}
                  </Button>
                  {/* Sixième chemin vers /reserver-en-ligne, en plus des cinq
                      déjà instrumentés (cahier des charges, tâche 16) : même
                      raisonnement que pour le CTA du header, l’omettre
                      fausserait la répartition entre emplacements — ce qui est
                      précisément ce que la mesure cherche à établir. */}
                  <Link
                    to="/reserver-en-ligne"
                    className="services-impasse-lien"
                    onClick={() => bookingCtaClicked('services_impasse')}
                  >
                    Ou réserver en ligne
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default Services
